import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

interface GeographyOption {
  readonly value: string;
  readonly search: string;
}

const PRIORITY_OPTIONS = [
  'Riyadh, Saudi Arabia',
  'Jeddah, Saudi Arabia',
  'Dammam, Saudi Arabia',
  'Makkah, Saudi Arabia',
  'Madinah, Saudi Arabia',
  'Dubai, United Arab Emirates',
  'Abu Dhabi, United Arab Emirates',
  'Kuwait City, Kuwait',
  'Doha, Qatar',
  'Manama, Bahrain',
  'Muscat, Oman',
] as const;

function normalized(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/gu, '')
    .trim()
    .toLocaleLowerCase('en');
}

const priorityIndex: readonly GeographyOption[] = PRIORITY_OPTIONS.map((value) => ({
  value,
  search: normalized(value),
}));

let worldIndexPromise: Promise<readonly GeographyOption[]> | null = null;

function loadWorldIndex(): Promise<readonly GeographyOption[]> {
  worldIndexPromise ??= Promise.all([
    import('countries-states-cities-service/cities'),
    import('countries-states-cities-service/countries'),
  ]).then(([{ Cities }, { Countries }]) => {
    const countries = new Map(
      Countries.getCountries().map((country) => [country.iso2, country.name]),
    );
    const seen = new Set<string>();
    const options: GeographyOption[] = [];
    for (const city of Cities.getCities()) {
      const country = countries.get(city.country_code);
      if (country === undefined) continue;
      const value = `${city.name}, ${country}`;
      const key = normalized(value);
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ value, search: key });
    }
    return options;
  });
  return worldIndexPromise;
}

function matches(
  options: readonly GeographyOption[],
  query: string,
): readonly GeographyOption[] {
  const needle = normalized(query);
  if (needle.length === 0) return options.slice(0, 8);
  const exactPrefix: GeographyOption[] = [];
  const wordPrefix: GeographyOption[] = [];
  for (const option of options) {
    if (option.search.startsWith(needle)) exactPrefix.push(option);
    else if (option.search.includes(` ${needle}`)) wordPrefix.push(option);
    if (exactPrefix.length >= 8) break;
  }
  return [...exactPrefix, ...wordPrefix].slice(0, 8);
}

export function GeographyCombobox({
  label = 'Geography',
  value,
  invalid = false,
  onChange,
}: {
  readonly label?: string;
  readonly value: string;
  readonly invalid?: boolean;
  readonly onChange: (value: string) => void;
}): ReactNode {
  const id = useId();
  const listId = `${id}-geography-listbox`;
  const [worldIndex, setWorldIndex] = useState<readonly GeographyOption[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const ensureWorldIndex = () => {
    void loadWorldIndex().then((index) => {
      if (mounted.current) setWorldIndex(index);
    });
  };
  const options = useMemo(
    () => matches(worldIndex ?? priorityIndex, value),
    [value, worldIndex],
  );

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setActive(0);
  };

  return (
    <label
      className={`mj-business-profile-edit-field mj-geography-combobox${
        invalid ? ' is-missing' : ''
      }`}
    >
      {label}
      <span className="mj-geography-control">
        <input
          role="combobox"
          aria-invalid={invalid || undefined}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open && options.length > 0}
          aria-activedescendant={
            open && options[active] !== undefined
              ? `${listId}-${String(active)}`
              : undefined
          }
          autoComplete="off"
          value={value}
          onFocus={() => {
            setOpen(true);
            ensureWorldIndex();
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setOpen(false);
            }, 100);
          }}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setActive(0);
            ensureWorldIndex();
          }}
          onKeyDown={(event) => {
            if (!open || options.length === 0) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActive((current) => (current + 1) % options.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActive((current) => (current - 1 + options.length) % options.length);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const selected = options[active];
              if (selected !== undefined) choose(selected.value);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        />
        {open && options.length > 0 ? (
          <ul id={listId} role="listbox" className="mj-geography-options">
            {options.map((option, index) => (
              <li
                id={`${listId}-${String(index)}`}
                key={option.value}
                role="option"
                aria-selected={index === active}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option.value);
                }}
              >
                {option.value}
              </li>
            ))}
          </ul>
        ) : null}
      </span>
    </label>
  );
}
